import { inject, Injectable } from '@angular/core';
import {
  IxNgAppVersionService,
  IxNgBusyFormService,
  IxNgHtmlStaticUtils,
  IxServiceBaseUtil,
} from 'ix-ng-components';
import {
  IxExceptionMessage,
  IxNgErrorHandlingService,
  IxNgValidationService,
  IxValidationException,
} from 'ix-ng-forms';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of, share, Subject, Subscription } from 'rxjs';
import { IxControlSettingsService } from 'ix-ng-core';
import { IxNgRoutingService } from 'ix-ng-routing';
import { IxKeycloakHttp, IxNgHttpService } from 'ix-ng-http';
import * as wjcCore from '@grapecity/wijmo';
import { Maybe } from 'ix-ng-static-utils';
import { ErrorReportingDialogComponent } from '../error-reporting-dialog/error-reporting-dialog.component';
import { ValidationDialogService } from '../validation/validation-dialog/validation-dialog.service';

export class AccessData {
  actionMap = new Map<string, boolean>();
  observable: Subject<boolean> | undefined;
}

export interface ErrorReportingData {
  errorList: IxExceptionMessage[];
  appService: BaseAppService;
}

@Injectable({
  providedIn: 'root',
})
export class BaseAppService extends IxNgAppVersionService {
  protected ixNgRoutingService = inject(IxNgRoutingService);
  protected ixKeycloakHttp = inject(IxKeycloakHttp);
  protected ixNgErrorHandlingService = inject(IxNgErrorHandlingService);
  protected ixNgHttpService = inject(IxNgHttpService);
  protected ixNgBusyFormService = inject(IxNgBusyFormService);
  protected dialogService = inject(DialogService);
  protected validationDialogService = inject(ValidationDialogService);
  protected ixNgValidationService = inject(IxNgValidationService);
  protected ixControlSettingsService = inject(IxControlSettingsService);

  private _accessService: IxServiceBaseUtil;

  private _errorReportingDialog: DynamicDialogRef | null = null;
  private _errorReportDialogSubscription: Subscription | null = null;
  public static controllerUrl: string | undefined = '';

  public objectActionMap = new Map<string, AccessData>();

  private displayDialogSource = new Subject<{ header: string; message: string }>();
  displayDialogSource$ = this.displayDialogSource.asObservable();

  constructor() {
    super();

    //Lookup ob prvem odpiranju ne filtrira kar po izbrani vrstici ampak prikaže vse vrednosti (torej brez filtriranja po tekstu)
    this.ixControlSettingsService.controlSettings.defaultDropdownMode = 'blank';

    IxNgHtmlStaticUtils.breakPointWidth = 992;

    // Inicializacija procedur
    this.ixNgErrorHandlingService.handleValidationError = (
      error: IxValidationException,
      ignoreWarningsAction?: () => void,
    ) => {
      this.validationDialogService.handleSubscriptionErrorCallback(error, ignoreWarningsAction);
    };

    this.ixNgErrorHandlingService.errorReportObservable$.subscribe(() => {
      this.addExceptionError();
    });

    this.ixNgValidationService.getFlexGridMessage = (
      stVsehZapisov: Maybe<number>,
      stPrikazanihZapisov: Maybe<number>,
    ) => {
      if (stVsehZapisov === 0) {
        return 'Ni zapisov';
      } else {
        return 'Prikazanih: ' + stPrikazanihZapisov + ' od ' + stVsehZapisov;
      }
    };

    this._accessService = new IxServiceBaseUtil(
      this.ixKeycloakHttp,
      this.ixNgRoutingService,
      this.ixNgErrorHandlingService,
      this.ixNgHttpService,
      this.ixNgBusyFormService,
    );
    this._accessService.serviceUrl = BaseAppService.controllerUrl + `/security/SecSecurity`;

    if (wjcCore) {
      wjcCore.setLicenseKey(
        'prod-arsktrp.ixtlan-team.si|ekmetijstvo.sigov.si|ekmetijstvo-api-test.gov.si|test-arsktrp.ixtlan-team.si|razvoj-arsktrp.ixtlan-team.si|ekmetijstvo-test.sigov.si|ekmetijstvo.gov.si|prod-ext-arsktrp.ixtlan-team.si|test-ext-arsktrp.ixtlan-team.si|razvoj-ext-arsktrp.ixtlan-team.si,391186295892465#B0Nb6l6cu2md4NnapRXZttWZsk6cu4WYlRXLuFGb4hXauAnc4t6cyFWLk3mcwJiOiMXbEJCLi8ybu2mLkBSbhVGVg8WYsRHeJJiOiEmTDJCLiUjN4ITO8UTOyYDOxETOzIiOiQWSiwSfdtlOicGbmJCLiEjdzIDMyIiOiIXZ6JCLlNHbhZmOiI7ckJye0ICbuFkI1pjIEJCLi4TPRNHcromRBljNCF7U4ElTn56K4JmMQBXcUZmWHdDeUJ6VHdnQ5JDcNl7ZMJnc4FVTQVVRjZXc7lmarZFSmtyLaV4RiRjRuB5Z0plMz84bGd6S0hle426arUEMysET9wUeERUN4RXNv2yVZdzNGxmN4ZEM5IVTUBjYJdlVNxWNaBHN8gWSCRXUwVkbCJnR8EHTQdVS5l7K9QmS7RmezFmTZJjV5VFMhNkazZTSlFWUutmMmFzZCdzbBlGcjBFezwEUnN7L4FnRRFDc4VEM8kTcNxUTrw4SJ5UYyknc496Z0JjYnJ6MykFeSJ6KsRmarhWcuJjeQNHdWp4SrZzURpGTyFEasZXT9Q6R8VHT7ZTdYhXdxg6ToJlbVV5U7djSpRlS9o4YwkXMuhnYUlmeORzQ4YlVaZVczkmepVTTUR4S8lkNup5aqVldHdmaLZTbXdTYRJzatJUaiojITJCLiEUN7AzQ4UUMiojIIJCL4AzNyYjM9kDN0IicfJye=#Qf35VfikEMyIlI0IyQiwiIu3Waz9WZ4hXRgACdlVGaThXZsZEIv5mapdlI0IiTisHL3JSNJ9UUiojIDJCLi86bpNnblRHeFBCIyV6dllmV4J7bwVmUg2Wbql6ViojIOJyes4nILdDOIJiOiMkIsIibvl6cuVGd8VEIgc7bSlGdsVXTg2Wbql6ViojIOJyes4nI4YkNEJiOiMkIsIibvl6cuVGd8VEIgAVQM3EIg2Wbql6ViojIOJyes4nIzMEMCJiOiMkIsISZy36Qg2Wbql6ViojIOJyes4nIVhzNBJiOiMkIsIibvl6cuVGd8VEIgQnchh6QsFWaj9WYulmRg2Wbql6ViojIOJyebpjIkJHUiwiI9IDMyYDMgYDM5ATNyAjMiojI4J7QiwiIpNnLtFWZ45ibhxGd8lmLwJHdrNnch5Cd8VWLq3md0Fmcsk6cu4WYlRXLuFGb4hXauAnc4t6cyFWL4hXZtQ7clRHLpNnLtFWZ45ibhxGd8lmLwJHdrNnch5Cd8VWLk3mcwxSaz9idvdmLvZHdzpWa4VWbrVGLpNnL636ZpNnL4NXZ45yb6R7cqlGdl56alxSaz9SbhVGdt8WYsRHep9CcyR7azJXYto6b6pXYyxSaz9SbhVGdt8WYsRHep9CcyR7azJXYtQ7clRHLpNnL636ZuQ7clRXLpBXYt2md4NnapRXZttWZskUc5Y',
      );
    }
  }

  public addExceptionError() {
    this._errorReportingDialog = this.dialogService.open(ErrorReportingDialogComponent, {
      header: 'Napaka na strani',
      style: { 'max-width': '99vw', width: '1200px', 'max-height': '95vh' },
      styleClass: 'CssErrorReportingDialog',
      showHeader: false,
      data: {
        errorList: this.ixNgErrorHandlingService.errorList,
        appService: this,
      },
    });
    if (this._errorReportDialogSubscription) {
      this._errorReportDialogSubscription.unsubscribe();
    }

    if (this._errorReportingDialog) {
      this._errorReportDialogSubscription = this._errorReportingDialog.onDestroy.subscribe(() => {
        this.ixNgErrorHandlingService.errorReportDialogOpen = false;
        this.ixNgErrorHandlingService.errorList = [];
      });
    }
  }

  hasAccess(objectCode: string, action?: string): Observable<boolean> {
    if (objectCode) {
      let accessData = this.objectActionMap.get(objectCode);
      if (accessData && !accessData.observable) {
        if (action) {
          return of(accessData.actionMap.has(action));
        }
        return of(accessData.actionMap.keys.length > 0);
      } else if (accessData && accessData.observable) {
        return accessData.observable;
      } else {
        accessData = new AccessData();
        // accessData.observable = new Subject().share() as Subject<boolean>;
        accessData.observable = new Subject<boolean>().pipe(share()) as Subject<boolean>;
        this.objectActionMap.set(objectCode, accessData);
        const ixServiceBaseUtil = new IxServiceBaseUtil(
          this.ixKeycloakHttp,
          this.ixNgRoutingService,
          this.ixNgErrorHandlingService,
          this.ixNgHttpService,
          this.ixNgBusyFormService,
        );
        ixServiceBaseUtil.serviceUrl = BaseAppService.controllerUrl + `/security/SecSecurity`;

        ixServiceBaseUtil.safeCall(
          () =>
            ixServiceBaseUtil.get<Record<string, string[]>>(`readPrivilegeMap`, {
              objectCodeSeznamString: objectCode,
            }),
          (x: undefined | null | Record<string, string[]>): void => {
            const subject = accessData!.observable;
            accessData!.observable = undefined;
            let accessDataInternal: AccessData | undefined;
            if (x) {
              if (x['NEDOSTOP'] !== undefined) {
                // Api je vrnil da nimamo pravic ker gre za primer nedostopa.
                // Do tega je prišlo ker uporabnik še ni prejel notificationa preko websocket.
                // Nastavimo dummy vrednost v sporočilo da uporabnika vseeno pravilno preusmeri na nedostop in zaklene app
                return;
              }
              Object.getOwnPropertyNames(x).forEach((y) => {
                accessDataInternal = new AccessData();
                x[y].forEach((z) => {
                  accessDataInternal!.actionMap.set(z, true);
                });
                this.objectActionMap.set(y, accessDataInternal);
              });
            }
            accessDataInternal = this.objectActionMap.get(objectCode);
            if (accessDataInternal && accessDataInternal.actionMap) {
              if (action) {
                subject!.next(accessDataInternal.actionMap.has(action));
              } else {
                subject!.next(accessDataInternal.actionMap.keys.length > 0);
              }
            } else {
              subject!.next(false);
            }
          },
        );
        return accessData.observable;
      }
    }
    return of(false);
  }
}
